import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/Layout/PageHeader';
import StatsCard from '@/components/Dashboard/StatsCard';
import ClinicCard from '@/components/Dashboard/ClinicCard';
import AlertCard from '@/components/Dashboard/AlertCard';
import RevenueChart from '@/components/Dashboard/RevenueChart';
import { dashboardAPI, aiAPI, invoicesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCurrency } from '@/lib/utils';
import { 
  CurrencyInr, 
  Receipt, 
  TrendUp, 
  Bank,
  ArrowClockwise,
  CalendarBlank,
  ChartBar,
  Invoice,
  Warning,
  ArrowSquareOut,
  ShoppingCart,
} from '@phosphor-icons/react';

const MONTHS = [
  { value: '1', label: 'January' }, { value: '2', label: 'February' },
  { value: '3', label: 'March' }, { value: '4', label: 'April' },
  { value: '5', label: 'May' }, { value: '6', label: 'June' },
  { value: '7', label: 'July' }, { value: '8', label: 'August' },
  { value: '9', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const FY_OPTIONS = [
  { value: '2026-27', label: 'FY 2026-27' },
  { value: '2025-26', label: 'FY 2025-26' },
  { value: '2024-25', label: 'FY 2024-25' },
  { value: '2023-24', label: 'FY 2023-24' },
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dailySummary, setDailySummary] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [overview, setOverview] = useState(null);
  const [clinicPerformance, setClinicPerformance] = useState([]);
  const [alerts, setAlerts] = useState([]);
  
  const now = new Date();
  const [selectedDate, setSelectedDate] = useState(now.toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [selectedFY, setSelectedFY] = useState('2026-27');
  const [viewMode, setViewMode] = useState('monthly');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sourceOptions, setSourceOptions] = useState([]);

  // Load source options on mount
  useEffect(() => {
    invoicesAPI.getMeta().then(res => {
      setSourceOptions(res.data.sources || []);
    }).catch(() => {});
  }, []);

  // Helper: get date range for current view
  const getDateRange = useCallback(() => {
    if (viewMode === 'daily') {
      return { start: selectedDate, end: selectedDate };
    } else if (viewMode === 'monthly') {
      const m = selectedMonth.padStart(2, '0');
      const start = `${selectedYear}-${m}-01`;
      const nextM = parseInt(selectedMonth) === 12 ? 1 : parseInt(selectedMonth) + 1;
      const nextY = parseInt(selectedMonth) === 12 ? parseInt(selectedYear) + 1 : parseInt(selectedYear);
      const end = `${nextY}-${String(nextM).padStart(2, '0')}-01`;
      return { start, end };
    } else {
      const fyStart = parseInt(selectedFY.split('-')[0]);
      return { start: `${fyStart}-04-01`, end: `${fyStart + 1}-03-31` };
    }
  }, [viewMode, selectedDate, selectedMonth, selectedYear, selectedFY]);

  // Drill-down navigation helpers
  const drillToInvoices = (params) => {
    const q = new URLSearchParams(params).toString();
    navigate(`/invoices?${q}`);
  };

  const drillRevenue = () => {
    const { start, end } = getDateRange();
    drillToInvoices({ start_date: start, end_date: end, title: `Revenue Drill Down` });
  };

  const drillOutstanding = () => {
    drillToInvoices({ status: 'overdue', start_date: '', end_date: '', title: 'Outstanding (Overdue) Invoices' });
  };

  const drillInvoiceCount = () => {
    const { start, end } = getDateRange();
    drillToInvoices({ start_date: start, end_date: end, title: 'All Invoices' });
  };

  const drillStatus = (status) => {
    const { start, end } = getDateRange();
    drillToInvoices({ status, start_date: start, end_date: end, title: `${status.charAt(0).toUpperCase() + status.slice(1)} Invoices` });
  };

  const drillClinic = (clinic) => {
    const { start, end } = getDateRange();
    drillToInvoices({ clinic_id: clinic.clinic_id, clinic_name: clinic.name, start_date: start, end_date: end, title: clinic.name });
  };

  const drillCustomer = (customer) => {
    drillToInvoices({ customer: customer.name, start_date: '', end_date: '', title: `Customer: ${customer.name}` });
  };

  const drillMonth = (monthKey) => {
    // monthKey is like "2026-03"
    const [y, m] = monthKey.split('-');
    const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
    drillToInvoices({ start_date: `${monthKey}-01`, end_date: `${monthKey}-${lastDay}`, title: `Month: ${monthKey}` });
  };

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const srcParam = sourceFilter !== 'all' ? sourceFilter : undefined;
      const [dailyRes, monthlyRes, clinicRes, alertsRes, overviewRes] = await Promise.allSettled([
        dashboardAPI.getDailySummary(selectedDate, srcParam),
        dashboardAPI.getMonthlySummary(selectedMonth, selectedYear, srcParam),
        dashboardAPI.getClinicPerformance({ start_date: start, end_date: end, source: srcParam }),
        aiAPI.getAlerts(),
        dashboardAPI.getOverview({ financial_year: selectedFY, source: srcParam }),
      ]);
      if (dailyRes.status === 'fulfilled') setDailySummary(dailyRes.value.data);
      if (monthlyRes.status === 'fulfilled') setMonthlySummary(monthlyRes.value.data);
      if (clinicRes.status === 'fulfilled') setClinicPerformance(clinicRes.value.data.clinics || []);
      if (alertsRes.status === 'fulfilled') setAlerts(alertsRes.value.data.alerts || []);
      if (overviewRes.status === 'fulfilled') setOverview(overviewRes.value.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedMonth, selectedYear, selectedFY, sourceFilter, getDateRange]);

  useEffect(() => { loadDashboardData(); }, [loadDashboardData]);

  const hasData = (monthlySummary?.total_revenue > 0) || (monthlySummary?.net_revenue > 0) || (monthlySummary?.sales_orders_total > 0) || (overview?.revenue > 0) || (overview?.net_revenue > 0) || (overview?.sales_orders_total > 0) || (dailySummary?.daily_revenue > 0);

  if (loading) {
    return (
      <div data-testid="dashboard-page">
        <PageHeader title="Dashboard" subtitle="Loading financial overview..." />
        <div className="page-content">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white border border-[#E2E8F0] rounded-lg p-5">
                <div className="skeleton h-4 w-24 mb-3 rounded" />
                <div className="skeleton h-8 w-32 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <PageHeader 
        title="Dashboard" 
        subtitle="Financial Overview — Click any number to drill down"
        actions={
          <Button variant="outline" onClick={loadDashboardData} data-testid="refresh-dashboard-btn">
            <ArrowClockwise size={16} className="mr-2" />
            Refresh
          </Button>
        }
      />
      
      <div className="page-content">
        {/* Date/Period Controls */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 mb-6" data-testid="dashboard-filters">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex bg-[#F8FAFC] rounded-lg p-0.5">
              {[
                { key: 'daily', label: 'Daily', icon: CalendarBlank },
                { key: 'monthly', label: 'Monthly', icon: ChartBar },
                { key: 'fy', label: 'Financial Year', icon: TrendUp },
              ].map(({ key, label, icon: Icon }) => (
                <button key={key} onClick={() => setViewMode(key)} data-testid={`view-mode-${key}`}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center gap-1.5 transition-colors ${viewMode === key ? 'bg-[#0F4C81] text-white' : 'text-[#64748B] hover:text-[#0F172A]'}`}>
                  <Icon size={14} />{label}
                </button>
              ))}
            </div>
            <div className="h-6 w-px bg-[#E2E8F0]" />
            {viewMode === 'daily' && (
              <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-44" data-testid="daily-date-picker" />
            )}
            {viewMode === 'monthly' && (
              <div className="flex gap-2">
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-36" data-testid="month-selector"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-24" data-testid="year-selector"><SelectValue /></SelectTrigger>
                  <SelectContent>{['2024', '2025', '2026'].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {viewMode === 'fy' && (
              <Select value={selectedFY} onValueChange={setSelectedFY}>
                <SelectTrigger className="w-40" data-testid="fy-selector"><SelectValue /></SelectTrigger>
                <SelectContent>{FY_OPTIONS.map(fy => <SelectItem key={fy.value} value={fy.value}>{fy.label}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <div className="h-6 w-px bg-[#E2E8F0]" />
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-44" data-testid="source-filter">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {sourceOptions.map(src => (
                  <SelectItem key={src} value={src}>{src}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!hasData && viewMode !== 'fy' ? (
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center">
            <Invoice size={48} className="mx-auto text-[#64748B] mb-4" />
            <h2 className="text-xl font-semibold font-['Plus_Jakarta_Sans'] text-[#0F172A] mb-2">No Data for Selected Period</h2>
            <p className="text-[#64748B]">Try selecting a different month/year, or sync data from Zoho.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* KPI Cards — Revenue = Invoices only (confirmed/realized) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatsCard
                title={viewMode === 'daily' ? "Today's Revenue" : viewMode === 'fy' ? "FY Revenue" : "Monthly Revenue"}
                value={viewMode === 'daily' ? dailySummary?.daily_revenue : viewMode === 'fy' ? overview?.revenue : monthlySummary?.total_revenue}
                icon={CurrencyInr}
                subtitle="Invoices (Confirmed)"
                onClick={drillRevenue}
              />
              <StatsCard
                title={viewMode === 'daily' ? "Today's Collections" : viewMode === 'fy' ? "FY Collections" : "Monthly Collections"}
                value={viewMode === 'daily' ? dailySummary?.daily_collections : viewMode === 'fy' ? overview?.collections : monthlySummary?.total_collections}
                icon={Bank}
                onClick={() => navigate('/payments')}
              />
              <StatsCard
                title="Invoices"
                value={viewMode === 'daily' ? dailySummary?.invoice_count : viewMode === 'fy' ? overview?.invoice_count : monthlySummary?.invoice_count}
                format="number"
                icon={Receipt}
                onClick={drillInvoiceCount}
              />
              <StatsCard
                title={viewMode === 'daily' ? "Day's Outstanding" : "Period Outstanding"}
                value={viewMode === 'daily' ? dailySummary?.day_outstanding : viewMode === 'fy' ? overview?.outstanding : monthlySummary?.outstanding_balance}
                icon={Warning}
                subtitle={viewMode === 'daily' ? `Cumulative: ₹${((dailySummary?.outstanding_receivables || 0) / 100000).toFixed(1)}L` : "Unpaid invoices (overdue + sent)"}
                onClick={drillOutstanding}
              />
            </div>

            {/* Revenue Breakdown: SO (Booked/Unrealized) + CN + Net Revenue */}
            {(() => {
              const src = viewMode === 'fy' ? overview : viewMode === 'monthly' ? monthlySummary : dailySummary;
              const soTotal = src?.sales_orders_total || 0;
              const cnTotal = src?.credit_notes_total || 0;
              const invRevenue = viewMode === 'daily' ? (src?.daily_revenue || 0) : (src?.total_revenue || src?.revenue || 0);
              const netRevenue = src?.net_revenue || invRevenue;
              const ibTotal = src?.inter_branch_transfers || 0;
              if (!src || (soTotal === 0 && cnTotal === 0 && ibTotal === 0)) return null;
              return (
                <div className="bg-white border border-[#E2E8F0] rounded-lg p-5" data-testid={`${viewMode}-revenue-breakdown`}>
                  <div className="flex items-center gap-2 mb-4">
                    <TrendUp size={18} className="text-[#0F4C81]" />
                    <h3 className="text-sm font-semibold font-['Plus_Jakarta_Sans'] text-[#0F172A]">Revenue Breakdown</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="cursor-pointer hover:bg-[#F8FAFC] rounded-lg p-3 -m-1 transition-colors" onClick={drillRevenue}>
                      <p className="text-[10px] uppercase tracking-wide text-[#64748B] mb-1">Invoiced Revenue</p>
                      <p className="text-lg font-bold text-[#0F172A]">{formatCurrency(invRevenue)}</p>
                      <p className="text-[10px] text-[#64748B]">Confirmed & Realized</p>
                    </div>
                    <div className="cursor-pointer hover:bg-emerald-50 rounded-lg p-3 -m-1 transition-colors border-l border-[#E2E8F0]" onClick={() => navigate('/sales-credits?tab=sales-orders')}>
                      <p className="text-[10px] uppercase tracking-wide text-emerald-600 mb-1">+ Open Sales Orders</p>
                      <p className="text-lg font-bold text-emerald-700" data-testid={`${viewMode}-so-total`}>{formatCurrency(soTotal)}</p>
                      <p className="text-[10px] text-emerald-600">{src?.sales_orders_count || 0} pending — Yet to be invoiced</p>
                    </div>
                    <div className="cursor-pointer hover:bg-red-50 rounded-lg p-3 -m-1 transition-colors border-l border-[#E2E8F0]" onClick={() => {
                      const { start, end } = getDateRange();
                      navigate(`/sales-credits?tab=credit-notes&start_date=${start}&end_date=${end}`);
                    }}>
                      <p className="text-[10px] uppercase tracking-wide text-red-500 mb-1">- Credit Notes</p>
                      <p className="text-lg font-bold text-red-600" data-testid={`${viewMode}-cn-total`}>{formatCurrency(cnTotal)}</p>
                      <p className="text-[10px] text-red-500">{src?.credit_notes_count || 0} notes</p>
                    </div>
                    <div className="bg-[#0F4C81] rounded-lg p-3 -m-1 text-center">
                      <p className="text-[10px] uppercase tracking-wide text-emerald-300 mb-1">= Net Revenue</p>
                      <p className="text-lg font-bold text-white" data-testid={`${viewMode}-net-revenue`}>{formatCurrency(netRevenue)}</p>
                      <p className="text-[10px] text-emerald-300">Inv + SO - CN</p>
                    </div>
                  </div>
                  {ibTotal > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#E2E8F0] flex items-center justify-between" data-testid={`${viewMode}-inter-branch-line`}>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-[#64748B]">Inter-branch Transfers <span className="text-[#94A3B8]">(internal — excluded from revenue)</span></span>
                        <span className="text-[10px] text-[#94A3B8]">{src?.inter_branch_count || 0} entries</span>
                      </div>
                      <span className="text-sm font-semibold text-[#94A3B8]" data-testid={`${viewMode}-inter-branch-total`}>{formatCurrency(ibTotal)}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Revenue Chart + Status Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <RevenueChart 
                  data={monthlySummary?.daily_breakdown || []}
                  title={viewMode === 'monthly' ? `Revenue vs Collections (${MONTHS[parseInt(selectedMonth)-1]?.label} ${selectedYear})` : 'Revenue Trend'}
                />
              </div>
              
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                <h3 className="text-base font-medium font-['Plus_Jakarta_Sans'] text-[#0F172A] mb-4">Status Breakdown</h3>
                <div className="space-y-3">
                  {Object.entries(monthlySummary?.status_breakdown || overview?.status_breakdown || {}).map(([status, data]) => (
                    <div 
                      key={status} 
                      className="flex items-center justify-between py-2 border-b border-[#E2E8F0] last:border-0 cursor-pointer hover:bg-[#F8FAFC] rounded px-2 -mx-2 transition-colors group"
                      onClick={() => drillStatus(status)}
                      data-testid={`drill-status-${status}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`status-pill ${status}`}>{status}</span>
                        <span className="text-xs text-[#64748B]">{data.count}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{formatCurrency(data.amount)}</span>
                        <ArrowSquareOut size={12} className="text-[#64748B] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ))}
                  {Object.keys(monthlySummary?.status_breakdown || overview?.status_breakdown || {}).length === 0 && (
                    <p className="text-sm text-[#64748B] text-center py-4">No data</p>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Stats + AI Alerts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                <h3 className="text-base font-medium font-['Plus_Jakarta_Sans'] text-[#0F172A] mb-4">Quick Stats</h3>
                <div className="space-y-2.5">
                  <div className="flex justify-between items-center py-1.5 border-b border-[#E2E8F0]">
                    <span className="text-sm text-[#64748B]">Avg. Invoice Value</span>
                    <span className="text-sm font-medium">{formatCurrency(monthlySummary?.avg_invoice_value || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-[#E2E8F0]">
                    <span className="text-sm text-[#64748B]">Max Invoice</span>
                    <span className="text-sm font-medium">{formatCurrency(monthlySummary?.max_invoice_value || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-[#E2E8F0]">
                    <span className="text-sm text-[#64748B]">GST Collected</span>
                    <span className="text-sm font-medium">{formatCurrency(monthlySummary?.total_gst || overview?.gst_collected || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-sm text-[#64748B]">Collection Rate</span>
                    <span className="text-sm font-medium">{(monthlySummary?.collection_rate || overview?.collection_rate || 0).toFixed(1)}%</span>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-medium font-['Plus_Jakarta_Sans'] text-[#0F172A]">AI Alerts</h3>
                  <span className="text-xs text-[#64748B]">{alerts.length} active</span>
                </div>
                <div className="space-y-3 max-h-[280px] overflow-y-auto">
                  {alerts.length > 0 ? alerts.map((alert, i) => <AlertCard key={i} alert={alert} />) : (
                    <div className="text-center py-6 text-[#64748B]"><p className="text-sm">No alerts at this time</p></div>
                  )}
                </div>
              </div>
            </div>

            {/* FY Mode: Monthly Trend + Top Customers — CLICKABLE */}
            {viewMode === 'fy' && overview?.monthly_trend?.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                  <h3 className="text-base font-medium font-['Plus_Jakarta_Sans'] text-[#0F172A] mb-4">Monthly Revenue Trend</h3>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {overview.monthly_trend.map((m) => {
                      const maxRev = Math.max(...overview.monthly_trend.map(t => t.revenue));
                      const pct = maxRev > 0 ? (m.revenue / maxRev) * 100 : 0;
                      return (
                        <div 
                          key={m.month} 
                          className="flex items-center gap-3 cursor-pointer hover:bg-[#F8FAFC] rounded px-2 -mx-2 py-1 transition-colors group"
                          onClick={() => drillMonth(m.month)}
                          data-testid={`drill-month-${m.month}`}
                        >
                          <span className="text-xs text-[#64748B] w-16 flex-shrink-0">{m.month}</span>
                          <div className="flex-1 bg-[#E2E8F0] rounded-full h-4 overflow-hidden">
                            <div className="bg-[#0F4C81] h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs font-medium w-24 text-right">{formatCurrency(m.revenue)}</span>
                          <ArrowSquareOut size={12} className="text-[#64748B] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                  <h3 className="text-base font-medium font-['Plus_Jakarta_Sans'] text-[#0F172A] mb-4">Top Customers</h3>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {(overview.top_customers || []).map((c, i) => (
                      <div 
                        key={i} 
                        className="flex items-center justify-between py-2 border-b border-[#E2E8F0] last:border-0 cursor-pointer hover:bg-[#F8FAFC] rounded px-2 -mx-2 transition-colors group"
                        onClick={() => drillCustomer(c)}
                        data-testid={`drill-customer-${i}`}
                      >
                        <div>
                          <p className="text-sm font-medium">{c.name}</p>
                          <p className="text-xs text-[#64748B]">{c.count} invoices</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#0F4C81]">{formatCurrency(c.revenue)}</span>
                          <ArrowSquareOut size={12} className="text-[#64748B] opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Clinic Performance — CLICKABLE */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-medium font-['Plus_Jakarta_Sans'] text-[#0F172A]">Clinic Performance</h3>
                {clinicPerformance.length > 0 && (
                  <span className="text-sm font-medium text-[#0F4C81]" data-testid="clinic-total-revenue">
                    Total: {formatCurrency(clinicPerformance.reduce((sum, c) => sum + (c.revenue || 0), 0))}
                    {' '}({clinicPerformance.reduce((sum, c) => sum + (c.invoice_count || 0), 0)} invoices)
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {clinicPerformance.map((clinic) => (
                  <ClinicCard 
                    key={clinic.name || clinic.clinic_id} 
                    clinic={clinic}
                    onClick={() => drillClinic(clinic)}
                  />
                ))}
                {clinicPerformance.length === 0 && (
                  <div className="col-span-full text-center py-8 text-[#64748B] bg-white border border-[#E2E8F0] rounded-lg">
                    <p className="text-sm">No clinic data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
